import { isDefined, isNotDefined } from '@sgme/fp';
import { TimeSeriesChart } from 'components/shared/Charts/TimeSeriesChart';
import { SeriesLineOptions } from 'highcharts';
import { zip } from 'lodash-es';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { correlationsChanged } from 'state/quotes/correlations/correlations.action';
import { Correlation } from 'state/quotes/correlations/correlations.model';
import { getCorrelationsByTabId } from 'state/quotes/correlations/correlations.selectors';
import { AppState } from 'state/state.model';

import { addConstant, addTable } from '../Graphs.helpers';
import { InputMap, TimeSeries, TimeSeriesAndTables } from '../Graphs.model';
import { CorrelationInputs } from '../shared/CorrelationInputs';

interface HistCorrelationsGraphProps {
  tabIndex?: number;
  tabId: string;
  data: TimeSeriesAndTables;
  useOppositeInterpolation?: boolean;
}

export const HistCorrelationsGraph: FC<HistCorrelationsGraphProps> = props => {
  const { tabId, tabIndex, data, useOppositeInterpolation = false } = props;

  const dispatch = useDispatch();
  const inputs = useSelector((state: AppState) => getCorrelationsByTabId(state, tabId));
  const options = useGraphOptions(data, inputs);

  const handleInputChanged = useCallback(
    (correlation: Omit<Correlation, 'saveCorrelation'>) => dispatch(correlationsChanged(tabId, correlation)),
    [tabId, dispatch],
  );

  return (
    <div className="d-flex flex-column gap-12px">
      <CorrelationInputs
        tabIndex={tabIndex}
        priceTable={data.tables['Prices']}
        correlation={inputs}
        useOppositeInterpolation={useOppositeInterpolation}
        onChanged={handleInputChanged}
      />
      <TimeSeriesChart seriesLineOptions={options} />
    </div>
  );
};

function constructTimeSeriesName(inputMap?: InputMap) {
  let name = 'HistoricalCorrelations';

  if (isNotDefined(inputMap)) {
    return name;
  }

  const isAsynchronous = inputMap['isAsynchronous'];
  if (isDefined(isAsynchronous)) {
    name = name.concat(isAsynchronous === true ? '.Y' : '.N');
  }

  const pickingFrequency = inputMap['pickingFrequency'];
  if (isDefined(pickingFrequency)) {
    name = name.concat(`.${pickingFrequency}`);
  }

  return name;
}

function addTimeSeries(timeSeries: TimeSeries): SeriesLineOptions[] {
  if (isNotDefined(timeSeries)) {
    return [];
  }

  return timeSeries.columns.map((col, i) => ({
    type: 'line',
    name: col.name,
    tooltip: { valueDecimals: 2 },
    visible: col.visible,
    data: zip(
      timeSeries.index,
      timeSeries.rows.map(row => Number(row[i])),
    ),
  }));
}

function useGraphOptions(data: TimeSeriesAndTables, inputs?: InputMap) {
  return useMemo(() => {
    const { timeSeries, tables } = data;
    const name = constructTimeSeriesName(inputs);
    const histCorrelationSeries = timeSeries[name];

    if (isNotDefined(histCorrelationSeries)) {
      return [];
    }

    const graphIndex = histCorrelationSeries.index;
    const askCorrelation = inputs?.['askCorrelation'] as number | undefined;
    const bidCorrelation = inputs?.['bidCorrelation'] as number | undefined;

    const ask = isDefined(askCorrelation) ? addConstant('Ask', askCorrelation, graphIndex) : [];
    const bid = isDefined(bidCorrelation) ? addConstant('Bid', bidCorrelation, graphIndex) : [];
    const series = addTimeSeries(histCorrelationSeries);

    const correlationsTable = tables['Correlations'];
    const constant = addTable(correlationsTable, graphIndex, [
      { name: 'Valo', label: 'RT Valo' },
      { name: 'TessaAsk', label: 'Tessa Ask', visible: false },
      { name: 'TessaBid', label: 'Tessa Bid', visible: false },
    ]);

    return [...series, ...constant, ...bid, ...ask];
  }, [data, inputs]);
}
